import { apiClient } from './client';
import { downloadPdfBlob } from '../utils/downloadPdf';
import {
  InternalAuditAttachment,
  InternalAuditChecklist,
  InternalAuditChecklistPayload,
  InternalAuditPlan,
  InternalAuditPlanPayload,
  InternalAuditReport,
  InternalAuditReportPayload,
} from '../types/internalAudit';
import { SequentialCodeSuggestion } from '../types/sequentialCode';

async function downloadBlob(url: string, fileName: string): Promise<void> {
  const { data } = await apiClient.get<Blob>(url, { responseType: 'blob' });
  const objectUrl = window.URL.createObjectURL(data);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.click();
  window.URL.revokeObjectURL(objectUrl);
}

async function uploadFiles(
  path: string,
  files: File[]
): Promise<InternalAuditAttachment[]> {
  const form = new FormData();
  files.forEach((file) => form.append('files', file));
  const { data } = await apiClient.post<InternalAuditAttachment[]>(path, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function fetchInternalAuditPlans(keyword?: string): Promise<InternalAuditPlan[]> {
  const { data } = await apiClient.get<InternalAuditPlan[]>('/internal-audits/plans', {
    params: keyword?.trim() ? { q: keyword.trim() } : undefined,
  });
  return data;
}

export async function fetchInternalAuditPlan(planId: number): Promise<InternalAuditPlan> {
  const { data } = await apiClient.get<InternalAuditPlan>(`/internal-audits/plans/${planId}`);
  return data;
}

export async function fetchNextInternalAuditPlanNo(): Promise<SequentialCodeSuggestion> {
  const { data } = await apiClient.get<SequentialCodeSuggestion>(
    '/internal-audits/plans/suggestions/plan-no'
  );
  return data;
}

export async function createInternalAuditPlan(
  payload: InternalAuditPlanPayload
): Promise<InternalAuditPlan> {
  const { data } = await apiClient.post<InternalAuditPlan>('/internal-audits/plans', payload);
  return data;
}

export async function updateInternalAuditPlan(
  planId: number,
  payload: InternalAuditPlanPayload
): Promise<InternalAuditPlan> {
  const { data } = await apiClient.patch<InternalAuditPlan>(
    `/internal-audits/plans/${planId}`,
    payload
  );
  return data;
}

export async function deleteInternalAuditPlan(planId: number): Promise<void> {
  await apiClient.delete(`/internal-audits/plans/${planId}`);
}

export async function approveInternalAuditPlan(planId: number): Promise<InternalAuditPlan> {
  const { data } = await apiClient.post<InternalAuditPlan>(
    `/internal-audits/plans/${planId}/approve`
  );
  return data;
}

export async function revokeInternalAuditPlan(planId: number): Promise<InternalAuditPlan> {
  const { data } = await apiClient.post<InternalAuditPlan>(
    `/internal-audits/plans/${planId}/revoke`
  );
  return data;
}

export async function uploadInternalAuditPlanAttachments(
  planId: number,
  files: File[]
): Promise<InternalAuditAttachment[]> {
  return uploadFiles(`/internal-audits/plans/${planId}/attachments`, files);
}

export async function deleteInternalAuditPlanAttachment(
  planId: number,
  attachmentId: number
): Promise<void> {
  await apiClient.delete(`/internal-audits/plans/${planId}/attachments/${attachmentId}`);
}

export async function downloadInternalAuditPlanAttachment(
  planId: number,
  attachmentId: number,
  fileName: string
): Promise<void> {
  await downloadBlob(
    `/internal-audits/plans/${planId}/attachments/${attachmentId}/download`,
    fileName
  );
}

export async function fetchInternalAuditChecklists(
  planId?: number
): Promise<InternalAuditChecklist[]> {
  const { data } = await apiClient.get<InternalAuditChecklist[]>('/internal-audits/checklists', {
    params: planId != null ? { planId } : undefined,
  });
  return data;
}

export async function fetchInternalAuditChecklist(
  checklistId: number
): Promise<InternalAuditChecklist> {
  const { data } = await apiClient.get<InternalAuditChecklist>(
    `/internal-audits/checklists/${checklistId}`
  );
  return data;
}

export async function fetchNextInternalAuditChecklistNo(): Promise<SequentialCodeSuggestion> {
  const { data } = await apiClient.get<SequentialCodeSuggestion>(
    '/internal-audits/checklists/suggestions/checklist-no'
  );
  return data;
}

export async function createInternalAuditChecklist(
  payload: InternalAuditChecklistPayload
): Promise<InternalAuditChecklist> {
  const { data } = await apiClient.post<InternalAuditChecklist>(
    '/internal-audits/checklists',
    payload
  );
  return data;
}

export async function updateInternalAuditChecklist(
  checklistId: number,
  payload: InternalAuditChecklistPayload
): Promise<InternalAuditChecklist> {
  const { data } = await apiClient.patch<InternalAuditChecklist>(
    `/internal-audits/checklists/${checklistId}`,
    payload
  );
  return data;
}

export async function deleteInternalAuditChecklist(checklistId: number): Promise<void> {
  await apiClient.delete(`/internal-audits/checklists/${checklistId}`);
}

export async function approveInternalAuditChecklist(
  checklistId: number
): Promise<InternalAuditChecklist> {
  const { data } = await apiClient.post<InternalAuditChecklist>(
    `/internal-audits/checklists/${checklistId}/approve`
  );
  return data;
}

export async function revokeInternalAuditChecklist(
  checklistId: number
): Promise<InternalAuditChecklist> {
  const { data } = await apiClient.post<InternalAuditChecklist>(
    `/internal-audits/checklists/${checklistId}/revoke`
  );
  return data;
}

export async function uploadInternalAuditChecklistAttachments(
  checklistId: number,
  files: File[]
): Promise<InternalAuditAttachment[]> {
  return uploadFiles(`/internal-audits/checklists/${checklistId}/attachments`, files);
}

export async function deleteInternalAuditChecklistAttachment(
  checklistId: number,
  attachmentId: number
): Promise<void> {
  await apiClient.delete(
    `/internal-audits/checklists/${checklistId}/attachments/${attachmentId}`
  );
}

export async function downloadInternalAuditChecklistAttachment(
  checklistId: number,
  attachmentId: number,
  fileName: string
): Promise<void> {
  await downloadBlob(
    `/internal-audits/checklists/${checklistId}/attachments/${attachmentId}/download`,
    fileName
  );
}

export async function fetchInternalAuditReports(planId?: number): Promise<InternalAuditReport[]> {
  const { data } = await apiClient.get<InternalAuditReport[]>('/internal-audits/reports', {
    params: planId != null ? { planId } : undefined,
  });
  return data;
}

export async function fetchInternalAuditReport(reportId: number): Promise<InternalAuditReport> {
  const { data } = await apiClient.get<InternalAuditReport>(
    `/internal-audits/reports/${reportId}`
  );
  return data;
}

export async function fetchNextInternalAuditReportNo(): Promise<SequentialCodeSuggestion> {
  const { data } = await apiClient.get<SequentialCodeSuggestion>(
    '/internal-audits/reports/suggestions/report-no'
  );
  return data;
}

export async function createInternalAuditReport(
  payload: InternalAuditReportPayload
): Promise<InternalAuditReport> {
  const { data } = await apiClient.post<InternalAuditReport>('/internal-audits/reports', payload);
  return data;
}

export async function updateInternalAuditReport(
  reportId: number,
  payload: InternalAuditReportPayload
): Promise<InternalAuditReport> {
  const { data } = await apiClient.patch<InternalAuditReport>(
    `/internal-audits/reports/${reportId}`,
    payload
  );
  return data;
}

export async function deleteInternalAuditReport(reportId: number): Promise<void> {
  await apiClient.delete(`/internal-audits/reports/${reportId}`);
}

export async function approveInternalAuditReport(reportId: number): Promise<InternalAuditReport> {
  const { data } = await apiClient.post<InternalAuditReport>(
    `/internal-audits/reports/${reportId}/approve`
  );
  return data;
}

export async function revokeInternalAuditReport(reportId: number): Promise<InternalAuditReport> {
  const { data } = await apiClient.post<InternalAuditReport>(
    `/internal-audits/reports/${reportId}/revoke`
  );
  return data;
}

export async function uploadInternalAuditReportAttachments(
  reportId: number,
  files: File[]
): Promise<InternalAuditAttachment[]> {
  return uploadFiles(`/internal-audits/reports/${reportId}/attachments`, files);
}

export async function deleteInternalAuditReportAttachment(
  reportId: number,
  attachmentId: number
): Promise<void> {
  await apiClient.delete(`/internal-audits/reports/${reportId}/attachments/${attachmentId}`);
}

export async function downloadInternalAuditReportAttachment(
  reportId: number,
  attachmentId: number,
  fileName: string
): Promise<void> {
  await downloadBlob(
    `/internal-audits/reports/${reportId}/attachments/${attachmentId}/download`,
    fileName
  );
}


export async function downloadInternalAuditPlanPdf(
  planId: number,
  fileName?: string
): Promise<void> {
  await downloadPdfBlob(
    `/internal-audits/plans/${planId}/pdf`,
    fileName || `내부감사계획_${planId}.pdf`
  );
}

export async function downloadInternalAuditChecklistPdf(
  checklistId: number,
  fileName?: string
): Promise<void> {
  await downloadPdfBlob(
    `/internal-audits/checklists/${checklistId}/pdf`,
    fileName || `내부감사체크리스트_${checklistId}.pdf`
  );
}

export async function downloadInternalAuditReportPdf(
  reportId: number,
  fileName?: string
): Promise<void> {
  await downloadPdfBlob(
    `/internal-audits/reports/${reportId}/pdf`,
    fileName || `내부감사보고서_${reportId}.pdf`
  );
}
